import { useEffect, useState } from "react";

/**
 * What the user sees while a streamed Claude answer is silent: the current
 * phase, a running clock, and the latest slice of Claude's summarized
 * reasoning so a long turn visibly makes progress. Callers swap it for the
 * streamed prose as soon as the first word arrives.
 *
 * Opus 5.5 thinks for minutes on a big document prompt before the first
 * word streams; without this the bubble is blank and a slow turn looks
 * identical to a dead one.
 */
export default function StreamProgress({
  label,
  thinking,
  startedAt,
  longRunningHint,
}: {
  /** Current phase text, e.g. "Loading 5 documents…". */
  label: string | null | undefined;
  /** Accumulated reasoning summary; only the tail is shown. */
  thinking: string;
  /** When the turn started, for the elapsed clock. */
  startedAt: number | null;
  /** Shown after 90s, for flows that are expected to run several minutes. */
  longRunningHint?: string;
}) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);
  const elapsed = startedAt ? Math.max(0, Math.floor((now - startedAt) / 1000)) : 0;
  const mm = Math.floor(elapsed / 60);
  const ss = String(elapsed % 60).padStart(2, "0");
  // The most recent thought is the useful one — trim to the last few
  // sentences so it reads as a status line, not a wall of text.
  const full = thinking.trim();
  const tail = full.slice(-320);
  return (
    <div className="text-sm text-slate-700 space-y-2">
      <div className="flex items-center gap-2">
        <Spinner />
        <span>{label || "Working…"}</span>
        <span className="text-xs text-slate-400 tabular-nums">
          {mm}:{ss}
        </span>
      </div>
      {tail && (
        <div className="text-xs text-slate-500 italic border-l-2 border-slate-200 pl-2 whitespace-pre-wrap">
          {tail.length < full.length ? "…" : ""}
          {tail}
        </div>
      )}
      {longRunningHint && elapsed >= 90 && (
        <div className="text-xs text-slate-500">{longRunningHint}</div>
      )}
    </div>
  );
}

function Spinner() {
  return (
    <svg className="animate-spin h-4 w-4 text-blue-700 shrink-0" viewBox="0 0 24 24" fill="none">
      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" opacity="0.25" />
      <path d="M4 12a8 8 0 018-8" stroke="currentColor" strokeWidth="4" strokeLinecap="round" />
    </svg>
  );
}
